/**
 * ImplementationGuide - SDK code generator and integration examples
 *
 * Features:
 * - Python, JavaScript, cURL code examples
 * - Control-specific event structures
 * - Best practices per control
 * - Copy-to-clipboard functionality
 */
import React, { useState } from 'react';
import { Card, Tabs, Button, Alert, Tag, Divider, message } from 'antd';
import { CopyOutlined, CodeOutlined, ApiOutlined, BookOutlined } from '@ant-design/icons';
import { colors, spacing } from '../../../theme/designTokens';

// Code templates for different languages
const generatePythonCode = (controlCode, eventStructure) => {
  const event = JSON.stringify(eventStructure, null, 2);
  return `import requests
import json
from datetime import datetime

# TrustGrid API Configuration
TRUSTGRID_API_URL = "http://localhost:5001/api/trustgrid"
PROJECT_ID = 3  # Your project ID
CONTROL_CODE = "${controlCode}"

# Sample event for ${controlCode}
event_data = ${event}

# Send event to TrustGrid
def send_trustgrid_event(event):
    """Send an event to TrustGrid for compliance monitoring"""
    try:
        response = requests.post(
            f"{TRUSTGRID_API_URL}/events",
            json={
                "project_id": PROJECT_ID,
                "control_code": CONTROL_CODE,
                **event
            },
            headers={"Content-Type": "application/json"}
        )
        response.raise_for_status()
        return response.json()
    except requests.exceptions.RequestException as e:
        print(f"Error sending event to TrustGrid: {e}")
        return None

# Example usage
if __name__ == "__main__":
    result = send_trustgrid_event(event_data)
    if result:
        print(f"Event sent successfully: {result}")
    else:
        print("Failed to send event")
`;
};

const generateJavaScriptCode = (controlCode, eventStructure) => {
  const event = JSON.stringify(eventStructure, null, 2);
  return `const axios = require('axios');

// TrustGrid API Configuration
const TRUSTGRID_API_URL = 'http://localhost:5001/api/trustgrid';
const PROJECT_ID = 3; // Your project ID
const CONTROL_CODE = '${controlCode}';

// Sample event for ${controlCode}
const eventData = ${event};

/**
 * Send an event to TrustGrid for compliance monitoring
 * @param {Object} event - The event data to send
 * @returns {Promise<Object>} API response
 */
async function sendTrustGridEvent(event) {
  try {
    const response = await axios.post(
      \`\${TRUSTGRID_API_URL}/events\`,
      {
        project_id: PROJECT_ID,
        control_code: CONTROL_CODE,
        ...event
      },
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error sending event to TrustGrid:', error.message);
    throw error;
  }
}

// Example usage
sendTrustGridEvent(eventData)
  .then(result => console.log('Event sent successfully:', result))
  .catch(error => console.error('Failed to send event:', error));
`;
};

const generateCurlCode = (controlCode, eventStructure) => {
  const event = JSON.stringify({
    project_id: 3,
    control_code: controlCode,
    ...eventStructure
  }, null, 2);

  return `# Send event to TrustGrid API for ${controlCode}

curl -X POST http://localhost:5001/api/trustgrid/events \\
  -H "Content-Type: application/json" \\
  -d '${event}'

# Alternative: Using environment variables

export TRUSTGRID_API_URL="http://localhost:5001/api/trustgrid"
export PROJECT_ID=3
export CONTROL_CODE="${controlCode}"

curl -X POST \${TRUSTGRID_API_URL}/events \\
  -H "Content-Type: application/json" \\
  -d '${event}'
`;
};

// Best practices for each control
const BEST_PRACTICES = {
  CTRL_01: [
    'Mask PII before sending events to TrustGrid',
    'Use secure regex patterns or NLP models for PII detection',
    'Store masked data separately with proper access controls',
    'Implement field-level encryption for sensitive data',
    'Regular audits of PII detection accuracy'
  ],
  CTRL_02: [
    'Conduct formal risk assessments before deployment',
    'Document risk classification methodology',
    'Implement human oversight for high-risk systems',
    'Review risk classifications quarterly',
    'Maintain audit trail of risk decisions'
  ],
  CTRL_03: [
    'Document explicit purposes before data collection',
    'Implement purpose limitation checks at ingestion',
    'Regular reviews of data usage vs. stated purposes',
    'User consent management for data collection',
    'Clear retention policies per purpose'
  ],
  CTRL_04: [
    'Track data lineage from source to model',
    'Maintain metadata for all training data',
    'Version control for datasets',
    'Data quality monitoring and validation',
    'Document data provenance in model cards'
  ],
  CTRL_05: [
    'Apply data minimization before training',
    'Remove unnecessary features and records',
    'Implement retention policies',
    'Regular data relevance reviews',
    'Automated minimization checks'
  ],
  CTRL_06: [
    'Continuous performance monitoring in production',
    'Baseline performance metrics at deployment',
    'Automated alerts for degradation',
    'Statistical drift detection',
    'Regular retraining schedules'
  ],
  CTRL_07: [
    'Test for multiple fairness metrics',
    'Include protected attributes in testing',
    'Implement bias mitigation techniques',
    'Regular fairness audits',
    'Transparent reporting of fairness metrics'
  ],
  CTRL_08: [
    'Define clear criteria for human review',
    'Track human override rates',
    'Document override rationales',
    'Regular reviewer training',
    'Feedback loops for model improvement'
  ],
  CTRL_09: [
    'Map data residency requirements by region',
    'Implement geofencing for data transfers',
    'Legal basis documentation for transfers',
    'Encryption for data in transit',
    'Regular compliance audits'
  ],
  CTRL_10: [
    'Version control for all model updates',
    'Impact assessments before deployment',
    'Rollback plans for each update',
    'Staged rollout procedures',
    'Post-deployment monitoring'
  ],
  CTRL_11: [
    'Maintain tested rollback procedures for all models',
    'Define clear incident response workflows',
    'Automated incident detection and alerting',
    'Regular incident response drills',
    'Document lessons learned from incidents'
  ],
  CTRL_12: [
    'Document explicit purposes before data collection',
    'Implement purpose limitation checks at ingestion',
    'Regular reviews of model usage vs. stated purposes',
    'User consent management for data processing',
    'Clear retention policies aligned with purpose'
  ],
  CTRL_13: [
    'Comprehensive data lineage tracking from source to model',
    'Maintain detailed metadata for all datasets',
    'Version control for datasets and transformations',
    'Data quality monitoring at each stage',
    'Document data provenance in model cards'
  ],
  CTRL_14: [
    'Apply data minimization before model training',
    'Remove unnecessary features and records',
    'Implement strict retention policies',
    'Regular data relevance reviews',
    'Automated minimization compliance checks'
  ],
  // Default for controls without specific best practices
  DEFAULT: [
    'Implement comprehensive logging for all AI operations',
    'Regular monitoring and auditing of control compliance',
    'Maintain detailed documentation of implementation',
    'Establish clear escalation procedures for violations',
    'Continuous improvement based on audit findings'
  ]
};

// Event field descriptions
const EVENT_FIELD_DESCRIPTIONS = {
  event_type: 'Type of event (model_invocation, model_deployment, data_collection, etc.)',
  model: 'Model name or identifier',
  provider: 'AI provider (openai, huggingface, aws, gcp, custom, etc.)',
  timestamp: 'Event timestamp in ISO 8601 format',
  event_metadata: 'Control-specific metadata (see control documentation for required fields)'
};

function ImplementationGuide({ controlCode, controlTitle }) {
  const [activeLanguage, setActiveLanguage] = useState('python');

  // Generate sample event structure based on control
  const getSampleEvent = () => {
    const baseEvent = {
      event_type: 'model_invocation',
      model: 'example-model',
      provider: 'openai',
      timestamp: new Date().toISOString(),
      event_metadata: {}
    };

    // Control-specific metadata examples
    const metadataExamples = {
      CTRL_01: { pii_detected: ['email', 'ssn'], pii_masked: true, detection_method: 'regex' },
      CTRL_02: { risk_level: 'high', risk_classification: 'high', human_oversight: true },
      CTRL_03: { data_purpose: 'training', user_consent: true, purpose_documented: true },
      CTRL_04: { data_source: 's3://bucket/data.csv', data_provenance: 'kaggle', lineage_tracked: true },
      CTRL_05: { data_minimized: true, unnecessary_features_removed: ['field1', 'field2'] },
      CTRL_06: { accuracy: 0.95, latency_ms: 120, error_rate: 0.02 },
      CTRL_07: { fairness_metrics: { demographic_parity: 0.1 }, bias_detected: false },
      CTRL_08: { human_reviewer: 'reviewer_id', human_decision: 'approve', ai_recommendation: 'approve' },
      CTRL_09: { source_region: 'us-east-1', destination_region: 'eu-west-1', cross_border_transfer: true },
      CTRL_10: { version: 'v1.2.0', impact_assessment_completed: true, rollback_plan_exists: true },
      CTRL_11: { incident_type: 'model_degradation', rollback_executed: true, rollback_time_seconds: 45, incident_severity: 'high' },
      CTRL_12: { stated_purpose: 'fraud_detection', actual_usage: 'fraud_detection', purpose_compliant: true, consent_obtained: true },
      CTRL_13: { data_lineage: ['raw_data', 'cleaned_data', 'training_set'], transformations: ['normalization', 'feature_engineering'], provenance_documented: true },
      CTRL_14: { original_features: 100, retained_features: 45, minimization_ratio: 0.55, retention_policy_days: 90 }
    };

    baseEvent.event_metadata = metadataExamples[controlCode] || {};
    return baseEvent;
  };

  const sampleEvent = getSampleEvent();
  const bestPractices = BEST_PRACTICES[controlCode] || BEST_PRACTICES.DEFAULT;

  const getCode = (language) => {
    switch (language) {
      case 'python':
        return generatePythonCode(controlCode, sampleEvent);
      case 'javascript':
        return generateJavaScriptCode(controlCode, sampleEvent);
      case 'curl':
        return generateCurlCode(controlCode, sampleEvent);
      default:
        return '';
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success('Code copied to clipboard');
  };

  const tabItems = [
    {
      key: 'python',
      label: (
        <span>
          <CodeOutlined /> Python
        </span>
      ),
      children: (
        <div style={{ position: 'relative' }}>
          <Button
            icon={<CopyOutlined />}
            style={{ position: 'absolute', top: spacing.sm, right: spacing.sm, zIndex: 1 }}
            onClick={() => copyToClipboard(getCode('python'))}
          >
            Copy
          </Button>
          <pre style={{
            background: '#1e1e1e',
            color: '#d4d4d4',
            padding: spacing.md,
            borderRadius: '4px',
            overflow: 'auto',
            fontSize: '13px',
            lineHeight: '1.5',
            maxHeight: '500px'
          }}>
            {getCode('python')}
          </pre>
        </div>
      )
    },
    {
      key: 'javascript',
      label: (
        <span>
          <CodeOutlined /> JavaScript
        </span>
      ),
      children: (
        <div style={{ position: 'relative' }}>
          <Button
            icon={<CopyOutlined />}
            style={{ position: 'absolute', top: spacing.sm, right: spacing.sm, zIndex: 1 }}
            onClick={() => copyToClipboard(getCode('javascript'))}
          >
            Copy
          </Button>
          <pre style={{
            background: '#1e1e1e',
            color: '#d4d4d4',
            padding: spacing.md,
            borderRadius: '4px',
            overflow: 'auto',
            fontSize: '13px',
            lineHeight: '1.5',
            maxHeight: '500px'
          }}>
            {getCode('javascript')}
          </pre>
        </div>
      )
    },
    {
      key: 'curl',
      label: (
        <span>
          <ApiOutlined /> cURL
        </span>
      ),
      children: (
        <div style={{ position: 'relative' }}>
          <Button
            icon={<CopyOutlined />}
            style={{ position: 'absolute', top: spacing.sm, right: spacing.sm, zIndex: 1 }}
            onClick={() => copyToClipboard(getCode('curl'))}
          >
            Copy
          </Button>
          <pre style={{
            background: '#1e1e1e',
            color: '#d4d4d4',
            padding: spacing.md,
            borderRadius: '4px',
            overflow: 'auto',
            fontSize: '13px',
            lineHeight: '1.5',
            maxHeight: '500px'
          }}>
            {getCode('curl')}
          </pre>
        </div>
      )
    }
  ];

  return (
    <Card>
      <div style={{ padding: spacing.lg }}>
        <Alert
          message={`Implementation Guide for ${controlCode}`}
          description={`Complete SDK examples and best practices for integrating ${controlTitle} into your AI/ML pipeline`}
          type="info"
          showIcon
          icon={<BookOutlined />}
          style={{ marginBottom: spacing.lg }}
        />

        <Tabs activeKey={activeLanguage} onChange={setActiveLanguage} items={tabItems} />

        <Divider />

        <h3 style={{ marginBottom: spacing.md, color: colors.text.primary }}>Event Structure</h3>
        <Card size="small" style={{ background: colors.background.secondary, marginBottom: spacing.lg }}>
          {Object.entries(EVENT_FIELD_DESCRIPTIONS).map(([field, description]) => (
            <div key={field} style={{ marginBottom: spacing.sm }}>
              <Tag color={colors.primary[500]} style={{ fontFamily: 'monospace' }}>{field}</Tag>
              <span style={{ color: colors.text.secondary }}>{description}</span>
            </div>
          ))}
        </Card>

        <Divider />

        <h3 style={{ marginBottom: spacing.md, color: colors.text.primary }}>Best Practices for {controlCode}</h3>
        <ul style={{ lineHeight: '2', color: colors.text.secondary }}>
          {bestPractices.map((practice, idx) => (
            <li key={idx}>{practice}</li>
          ))}
        </ul>

        <Divider />

        <Alert
          message="API Endpoint"
          description={
            <div>
              <strong>POST</strong> <code>http://localhost:5001/api/trustgrid/events</code>
              <br />
              <strong>Headers:</strong> <code>Content-Type: application/json</code>
              <br />
              <strong>Body:</strong> JSON object with project_id, control_code, and event data
            </div>
          }
          type="success"
          showIcon
          style={{ marginTop: spacing.md }}
        />
      </div>
    </Card>
  );
}

export default ImplementationGuide;
